/**
 * Shared Open House read + mutation helpers.
 *
 * Both the admin workspace and (indirectly, via the RPC) MAVIS read from
 * `open_house_snapshot(_event_id)` so the two surfaces can never disagree.
 * Do NOT compute totals in components — call `useOpenHouseSnapshot`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type EventStatus =
  | "draft" | "registration_open" | "registration_closed"
  | "completed" | "cancelled" | "archived" | "postponed";
export type ArrivalStatus = "expected" | "checked_in" | "no_show" | "cancelled";
export type FollowUpStatus = "none" | "needed" | "completed";
export type AttendeeType = "parent" | "teacher";
export type ReminderKind = "confirmation" | "seven_day" | "two_day" | "morning_of" | "follow_up" | "adhoc";
export type ReminderStatus = "scheduled" | "sending" | "sent" | "partial" | "failed" | "cancelled";
export type ReminderAudience = "parents" | "instructors" | "both";

export interface OpenHouseEvent {
  id: string;
  title: string;
  slug: string;
  event_date: string;
  start_time: string;
  end_time: string | null;
  timezone: string;
  location_name: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  parking_instructions: string | null;
  what_to_expect: string | null;
  children_welcome: boolean;
  registration_opens_at: string | null;
  registration_closes_at: string | null;
  rsvp_deadline: string | null;
  capacity_total: number | null;
  status: EventStatus;
  public_visible: boolean;
  audience: ReminderAudience;
  reminder_schedule: Record<string, boolean>;
  parent_reminder_copy: string | null;
  instructor_reminder_copy: string | null;
  duplicated_from_event_id: string | null;
  status_note: string | null;
  contact_email: string;
  end_time_approximate: boolean;
  created_at: string;
  updated_at: string;
}

/** Fields admins edit in the Add / Edit Open House form. */
export interface OpenHouseEventInput {
  title: string;
  event_date: string;
  start_time: string;
  end_time: string | null;
  end_time_approximate: boolean;
  location_name: string | null;
  address_line_1: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  what_to_expect: string | null;
  status: EventStatus;
  status_note: string | null;
  contact_email: string;
  capacity_total: number | null;
  public_visible: boolean;
}

export async function createEvent(input: OpenHouseEventInput, slug: string): Promise<OpenHouseEvent> {
  const user = (await supabase.auth.getUser()).data.user;
  const { data, error } = await (supabase as any)
    .from("open_house_events")
    .insert({ ...input, slug, created_by: user?.id ?? null })
    .select("*")
    .single();
  if (error) throw error;
  return data as OpenHouseEvent;
}

export async function updateEvent(id: string, input: OpenHouseEventInput): Promise<void> {
  const { error } = await (supabase as any).from("open_house_events").update(input).eq("id", id);
  if (error) throw error;
}

export async function countEventRegistrations(eventId: string): Promise<number> {
  const { count, error } = await (supabase as any)
    .from("open_house_registrations")
    .select("id", { count: "exact", head: true })
    .eq("event_id", eventId);
  if (error) throw error;
  return count ?? 0;
}

/** Deletes an open house. The database refuses if it has any RSVPs. */
export async function deleteEvent(id: string): Promise<void> {
  const { error } = await (supabase as any).from("open_house_events").delete().eq("id", id);
  if (error) throw error;
}

export interface Registration {
  id: string;
  event_id: string;
  attendee_type: AttendeeType;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  adult_count: number;
  child_count: number;
  additional_guest_count: number;
  total_headcount: number;
  instructor_status: string | null;
  how_heard: string | null;
  questions: string | null;
  communication_consent: boolean;
  arrival_status: ArrivalStatus;
  checked_in_at: string | null;
  is_walk_in: boolean;
  internal_notes: string | null;
  follow_up_status: FollowUpStatus;
  follow_up_completed_at: string | null;
  source: string;
  review_status: "new" | "reviewed";
  registration_status: string;
  created_at: string;
}

export interface ClassDemand {
  title: string;
  class_id: string | null;
  interest_type: "parent_interest" | "currently_teaches" | "interested_in_teaching";
  demand: number;
}

export interface Snapshot {
  event: OpenHouseEvent;
  total_headcount: number;
  parent_families: number;
  parent_heads: number;
  teacher_heads: number;
  walk_ins: number;
  checked_in: number;
  checked_in_heads: number;
  no_shows: number;
  cancelled: number;
  expected: number;
  follow_ups_needed: number;
  seats_remaining: number | null;
  capacity_total: number | null;
  new_since_last_review: number;
  class_demand: ClassDemand[];
}

export async function fetchEvents(): Promise<OpenHouseEvent[]> {
  const { data } = await (supabase as any)
    .from("open_house_events")
    .select("*")
    .order("event_date", { ascending: false });
  return (data ?? []) as OpenHouseEvent[];
}

export async function fetchSnapshot(eventId: string): Promise<Snapshot | null> {
  const { data, error } = await (supabase as any).rpc("open_house_snapshot", { _event_id: eventId });
  if (error) { console.error("open_house_snapshot", error); return null; }
  return (data ?? null) as Snapshot | null;
}

export async function fetchRegistrations(eventId: string): Promise<Registration[]> {
  const { data } = await (supabase as any)
    .from("open_house_registrations")
    .select("*")
    .eq("event_id", eventId)
    .order("created_at", { ascending: false });
  return (data ?? []) as Registration[];
}

export async function logAttendance(params: {
  event_id: string; registration_id?: string | null;
  kind: string; note?: string | null;
  before_snapshot?: unknown; after_snapshot?: unknown;
}) {
  const user = (await supabase.auth.getUser()).data.user;
  await (supabase as any).from("open_house_attendance_events").insert({
    event_id: params.event_id,
    registration_id: params.registration_id ?? null,
    kind: params.kind,
    actor_user_id: user?.id ?? null,
    note: params.note ?? null,
    before_snapshot: params.before_snapshot ?? null,
    after_snapshot: params.after_snapshot ?? null,
  });
}

export async function checkInRegistration(reg: Registration) {
  const before = { arrival_status: reg.arrival_status, checked_in_at: reg.checked_in_at };
  const user = (await supabase.auth.getUser()).data.user;
  const { error } = await (supabase as any)
    .from("open_house_registrations")
    .update({
      arrival_status: "checked_in",
      checked_in_at: new Date().toISOString(),
      checked_in_by: user?.id ?? null,
    })
    .eq("id", reg.id);
  if (error) throw error;
  await logAttendance({
    event_id: reg.event_id, registration_id: reg.id, kind: "checked_in",
    before_snapshot: before,
    after_snapshot: { arrival_status: "checked_in" },
  });
}

export async function markNoShow(reg: Registration) {
  const { error } = await (supabase as any)
    .from("open_house_registrations")
    .update({ arrival_status: "no_show" })
    .eq("id", reg.id);
  if (error) throw error;
  await logAttendance({
    event_id: reg.event_id, registration_id: reg.id, kind: "marked_no_show",
    before_snapshot: { arrival_status: reg.arrival_status },
    after_snapshot: { arrival_status: "no_show" },
  });
}

export async function resetArrival(reg: Registration) {
  const { error } = await (supabase as any)
    .from("open_house_registrations")
    .update({ arrival_status: "expected", checked_in_at: null })
    .eq("id", reg.id);
  if (error) throw error;
  await logAttendance({
    event_id: reg.event_id, registration_id: reg.id, kind: "arrival_reset",
    before_snapshot: { arrival_status: reg.arrival_status },
    after_snapshot: { arrival_status: "expected" },
  });
}

export async function adjustHeadcount(reg: Registration, next: { adult_count: number; child_count: number; additional_guest_count: number; }) {
  const before = { adult_count: reg.adult_count, child_count: reg.child_count, additional_guest_count: reg.additional_guest_count };
  const { error } = await (supabase as any)
    .from("open_house_registrations")
    .update(next)
    .eq("id", reg.id);
  if (error) throw error;
  await logAttendance({
    event_id: reg.event_id, registration_id: reg.id, kind: "headcount_adjusted",
    before_snapshot: before, after_snapshot: next,
  });
}

export async function addNote(reg: Registration, note: string) {
  const prev = reg.internal_notes ?? "";
  const stamped = `${prev ? prev + "\n" : ""}[${new Date().toLocaleString()}] ${note}`;
  const { error } = await (supabase as any)
    .from("open_house_registrations")
    .update({ internal_notes: stamped })
    .eq("id", reg.id);
  if (error) throw error;
  await logAttendance({
    event_id: reg.event_id, registration_id: reg.id, kind: "note_added", note,
  });
}

export async function markReviewed(reg: Registration) {
  await (supabase as any).from("open_house_registrations").update({ review_status: "reviewed" }).eq("id", reg.id);
}

export async function completeFollowUp(reg: Registration) {
  const user = (await supabase.auth.getUser()).data.user;
  const { error } = await (supabase as any).from("open_house_registrations").update({
    follow_up_status: "completed",
    follow_up_completed_at: new Date().toISOString(),
    follow_up_completed_by: user?.id ?? null,
  }).eq("id", reg.id);
  if (error) throw error;
  await logAttendance({ event_id: reg.event_id, registration_id: reg.id, kind: "follow_up_completed" });
}

export async function flagFollowUp(reg: Registration) {
  const { error } = await (supabase as any).from("open_house_registrations").update({
    follow_up_status: "needed",
  }).eq("id", reg.id);
  if (error) throw error;
  await logAttendance({ event_id: reg.event_id, registration_id: reg.id, kind: "follow_up_flagged" });
}

/** Live snapshot subscription: refreshes whenever the event's registrations or reminders change. */
export function useOpenHouseSnapshot(eventId: string | null) {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!eventId) { setSnapshot(null); setLoading(false); return; }
    const s = await fetchSnapshot(eventId);
    setSnapshot(s);
    setLoading(false);
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!eventId) return;
    const ch = supabase.channel(`oh-snap-${eventId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "open_house_registrations", filter: `event_id=eq.${eventId}` }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "open_house_reminders", filter: `event_id=eq.${eventId}` }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "open_house_events", filter: `id=eq.${eventId}` }, refresh)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [eventId, refresh]);

  return { snapshot, loading, refresh };
}

export function useOpenHouseRegistrations(eventId: string | null) {
  const [regs, setRegs] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!eventId) { setRegs([]); setLoading(false); return; }
    setRegs(await fetchRegistrations(eventId));
    setLoading(false);
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!eventId) return;
    const ch = supabase.channel(`oh-regs-${eventId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "open_house_registrations", filter: `event_id=eq.${eventId}` }, refresh)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [eventId, refresh]);

  return { regs, loading, refresh };
}

export function formatEventDate(iso: string) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
export function formatEventTime(start: string | null, end: string | null) {
  if (!start) return "";
  const to12 = (t: string) => {
    const [h, m] = t.split(":");
    const hh = parseInt(h, 10);
    const suf = hh >= 12 ? "PM" : "AM";
    const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
    return `${h12}:${m} ${suf}`;
  };
  return to12(start) + (end ? `–${to12(end)}` : "");
}

/** Compute default reminder schedule times for an event date. */
export function defaultReminderSchedule(eventDate: string, startTime: string) {
  const eventStart = new Date(`${eventDate}T${startTime}`);
  const dayBefore = (days: number) => {
    const d = new Date(eventStart);
    d.setDate(d.getDate() - days);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  };
  const morningOf = () => {
    const d = new Date(eventStart);
    d.setHours(8, 0, 0, 0);
    return d.toISOString();
  };
  const followUp = () => {
    const d = new Date(eventStart);
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d.toISOString();
  };
  return [
    { kind: "seven_day" as ReminderKind, scheduled_at: dayBefore(7) },
    { kind: "two_day" as ReminderKind, scheduled_at: dayBefore(2) },
    { kind: "morning_of" as ReminderKind, scheduled_at: morningOf() },
    { kind: "follow_up" as ReminderKind, scheduled_at: followUp() },
  ];
}
